import { useMemo, useState, useEffect, useRef, type ReactNode } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Building2, Landmark, MapPin, Briefcase, ChevronDown, Sparkles, ShieldCheck, AlertCircle, Handshake, Bookmark, MessageCircle, X, Lock, Mail, BadgeCheck, CreditCard, CheckCircle2, ArrowRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "@/lib/router-compat";
import AnimatedTotal from "@/components/tools/AnimatedTotal";
import LayerBreakdown from "@/components/tools/LayerBreakdown";
import { openHomie } from "@/lib/homieBridge";
import EmployerInlineAsk from "@/components/grants/EmployerInlineAsk";
import EmployerReferralFlow from "@/components/employer/EmployerReferralFlow";
import { supabase } from "@/integrations/supabase/client";
import {
  scoreProgram,
  hasQualificationData,
  STATUS_CLASSES,
  type QualificationProfile,
} from "@/lib/stackEligibility";
import { isAmountLike, benefitDescription } from "@/lib/stackAmount";
import { getHeroCategory, isHeroStackFunnel, getHeroStackPhoneProvided, HBA_AVERAGE_REWARD } from "@/lib/heroCategory";
import { trackEvent } from "@/lib/analytics";
import TopLenderHandoff from "@/components/grants/TopLenderHandoff";
import PhoneCapturePrompt from "@/components/dashboard/PhoneCapturePrompt";
import HbaWebinarInvite from "@/components/dashboard/HbaWebinarInvite";
import StackProfileQuestion from "@/components/grants/StackProfileQuestion";


export type Layer = "federal" | "state" | "county" | "employer";
export interface StackProgram {
  id?: string;
  layer: Layer;
  name: string;
  provider?: string | null;
  amount_text?: string | null;
  amount_min?: number | null;
  amount_max?: number | null;
  eligibility_summary?: string | null;
  stackable_with?: string[];
  url?: string | null;
  source: "admin" | "ai";
  verified?: boolean;
}
export interface StackResults {
  zip: string;
  state: string | null;
  employer_name: string | null;
  employer_status: "verified" | "inferred" | "unknown";
  layers: Record<Layer, StackProgram[]>;
  total_estimated: number;
}

const LAYER_META: Record<Layer, { label: string; icon: any; tagline: string; color: string }> = {
  federal: { label: "Federal", icon: Landmark, tagline: "Nationwide programs (FHA, VA, USDA)", color: "text-blue-600" },
  state: { label: "State", icon: Building2, tagline: "State housing finance agency", color: "text-emerald-600" },
  county: { label: "County / City", icon: MapPin, tagline: "Local down payment assistance", color: "text-amber-600" },
  employer: { label: "Employer", icon: Briefcase, tagline: "Your workplace benefit", color: "text-purple-600" },
};
const LAYER_BAR_COLOR: Record<Layer, string> = {
  federal: "#2563eb",
  state: "#059669",
  county: "#d97706",
  employer: "#7c3aed",
};
const ORDER: Layer[] = ["federal", "state", "county", "employer"];

function fmt(n: number) {
  return n >= 1000 ? `$${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k` : `$${n}`;
}
function programAmount(p: StackProgram): number {
  if (p.amount_max && p.amount_min) return Math.round((p.amount_min + p.amount_max) / 2);
  return p.amount_max || p.amount_min || 0;
}

export default function GrantStackResults({
  data,
  onSave,
  onEmployerAdded,
  qualificationPrompt,
  lifemart = false,
}: {
  data: StackResults;
  onSave?: () => void;
  onEmployerAdded?: (name: string) => void;
  qualificationPrompt?: ReactNode;
  /** When true, unlocks every program row, doubles the save-bonus, and hides
   *  the LifeMart upsell. When false, non-LifeMart users see the top 2
   *  programs per layer with the remainder blurred behind an unlock CTA. */
  lifemart?: boolean;
}) {
  const [openLayers, setOpenLayers] = useState<Record<Layer, boolean>>({
    federal: false, state: false, county: false, employer: false,
  });
  const [openProgram, setOpenProgram] = useState<string | null>(null);
  const [amountExplanationOpen, setAmountExplanationOpen] = useState(false);
  const [supportOpen, setSupportOpen] = useState(false);
  const [heroStackFlow, setHeroStackFlow] = useState(false);
  const [heroPhoneProvided, setHeroPhoneProvided] = useState(false);

  const layersRef = useRef<HTMLDivElement | null>(null);

  const toggle = (l: Layer) => {
    setOpenLayers((current) =>
      ORDER.reduce((next, layer) => ({ ...next, [layer]: layer === l ? !current[l] : false }), {} as Record<Layer, boolean>),
    );
    setOpenProgram(null);
  };

  /** "Review all programs": open every layer that has programs and scroll to the list. */
  const revealAllLayers = () => {
    setOpenLayers(
      ORDER.reduce(
        (next, layer) => ({ ...next, [layer]: data.layers[layer].length > 0 }),
        {} as Record<Layer, boolean>,
      ),
    );
    setOpenProgram(null);
    requestAnimationFrame(() => {
      layersRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  // Qualification answers (from /profile/complete or the emailed 5-question
  // check) turn "matched by ZIP" into a per-program qualified/likely read.
  const [qual, setQual] = useState<QualificationProfile | null>(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: auth } = await supabase.auth.getUser();
      if (!auth?.user) return;
      const { data: attrs } = await supabase
        .from("user_attributes")
        .select("income_range, household_size_bucket, is_first_time_buyer, down_payment_savings, purchase_timeline")
        .eq("user_id", auth.user.id)
        .maybeSingle();
      if (!cancelled) setQual((attrs as any) ?? null);
    })();
    return () => { cancelled = true; };
  }, []);
  const hasQual = hasQualificationData(qual);

  // Hero Stack funnel: layer 5 (Hero Benefits Alliance) rides on top of the
  // grant layers. It is never added into the summed total — it is an average
  // reward, not a matched program amount.
  const [heroCategory, setHeroCategoryState] = useState<string | null>(null);
  useEffect(() => {
    setHeroCategoryState(getHeroCategory());
    setHeroStackFlow(isHeroStackFunnel());
    setHeroPhoneProvided(getHeroStackPhoneProvided());
  }, []);


  const breakdown = useMemo(
    () =>
      ORDER.map((l) => ({
        label: LAYER_META[l].label,
        amount: data.layers[l].reduce((sum, p) => sum + programAmount(p), 0),
        count: data.layers[l].length,
        color: LAYER_BAR_COLOR[l],
      })).filter((row) => row.count > 0 || row.amount > 0),
    [data.layers],
  );

  const allProgramNames = useMemo(
    () => ORDER.flatMap((l) => data.layers[l].map((p) => p.name)).filter(Boolean),
    [data.layers],
  );

  /** How many real programs the run returned, regardless of dollar figures. */
  const programCount = useMemo(
    () => ORDER.reduce((n, l) => n + data.layers[l].length, 0),
    [data.layers],
  );
  const layersWithPrograms = useMemo(
    () => ORDER.filter((l) => data.layers[l].length > 0),
    [data.layers],
  );
  /** Most programs (all federal, all state, most county) carry no dollar figure.
   *  A $0 total means "amounts unpublished", never "nothing found" — so the
   *  hero, share card and next-steps rail fall back to a program count. */
  const hasAmounts = data.total_estimated > 0;


  const PREVIEW_PER_LAYER = 2;

  // Floor/ceiling total: non-LifeMart users see the sum of what's actually
  // visible (top 2 per layer) as their "preview total". The ceiling — the
  // real total — is gated. LifeMart members see the true total.
  const previewTotal = useMemo(() => {
    if (lifemart) return data.total_estimated;
    return ORDER.reduce((sum, l) => {
      const visible = data.layers[l].slice(0, PREVIEW_PER_LAYER);
      return sum + visible.reduce((s, p) => s + programAmount(p), 0);
    }, 0);
  }, [data.layers, data.total_estimated, lifemart]);
  const lockedAmount = Math.max(0, data.total_estimated - previewTotal);
  const displayedTotal = lifemart ? data.total_estimated : previewTotal;
  const lenderMatchHref = useMemo(() => {
    const qs = new URLSearchParams();
    qs.set("source", "stack");
    if (data.zip) qs.set("zip", data.zip);
    if (data.state) qs.set("state", data.state);
    allProgramNames.slice(0, 10).forEach((n) => qs.append("program", n));
    return `/lender-match?${qs.toString()}`;
  }, [allProgramNames, data.zip, data.state]);
  const topMatches = useMemo(
    () => ORDER.flatMap((layer) => data.layers[layer]).slice(0, 3),
    [data.layers],
  );
  const firstLayerWithPrograms = ORDER.find((layer) => data.layers[layer].length > 0) ?? "federal";
  const showHeroLayer = heroStackFlow || Boolean(heroCategory);
  const heroLayerLabel = heroCategory ? `average HBA reward for ${heroCategory}s` : "average HBA reward for eligible heroes";
  const claimHeroLayer = () => trackEvent("hero_stack_layer_claimed", { zip: data.zip, phone_provided: heroPhoneProvided });

  // ---- Proactive Homie nudge -------------------------------------------------
  // Detect the most actionable gap in the rendered Stack and surface a single,
  // dismissible pill that opens Homie pre-seeded with that specific gap.
  // Dedupe per snapshot (zip + total) via sessionStorage so it isn't nagging.
  const homieNudge = useMemo(() => {
    const employerEmpty = data.layers.employer.length === 0;
    const employerName = data.employer_name;
    const totalPrograms = ORDER.reduce((n, l) => n + data.layers[l].length, 0);
    const aiOnly = ORDER.every((l) =>
      data.layers[l].every((p) => p.source === "ai" || !p.verified),
    );
    const lowTotal = data.total_estimated > 0 && data.total_estimated < 8000;

    if (employerEmpty && employerName) {
      return {
        key: "employer-gap",
        label: `I don't see a homebuyer benefit at ${employerName} yet — want me to draft an HR ask?`,
        prompt: `My employer is ${employerName} and the Stack didn't surface a homebuyer benefit. Draft a short, polite email I can send HR asking if they offer down payment or closing cost assistance — and tell me what to do if they say no.`,
      };
    }
    if (data.layers.federal.length === 0) {
      return {
        key: "federal-gap",
        label: "No federal program showed up — should one apply to you?",
        prompt: `My Stack for ZIP ${data.zip} returned no federal program. Walk me through FHA, VA, and USDA eligibility in plain English and tell me which I should look into.`,
      };
    }
    if (lowTotal) {
      return {
        key: "low-total",
        label: `$${Math.round(data.total_estimated).toLocaleString()} feels low — what could unlock more?`,
        prompt: `My Stack total is only $${Math.round(data.total_estimated).toLocaleString()} for ZIP ${data.zip}. What factors (income, first-time buyer status, profession, ZIP) most often unlock additional grants I might be missing?`,
      };
    }
    if (aiOnly && totalPrograms > 0) {
      return {
        key: "verify",
        label: "Some of these are AI-sourced — want me to flag what to double-check with a lender?",
        prompt: `Some programs in my Stack are AI-sourced rather than verified. Which questions should I ask a lender or housing counselor to confirm each one is real and currently funded?`,
      };
    }
    return null;
  }, [data]);

  const nudgeKey = `homie_nudge:${data.zip}:${Math.round(data.total_estimated)}:${homieNudge?.key ?? "none"}`;
  const [nudgeDismissed, setNudgeDismissed] = useState<boolean>(() => {
    if (typeof window === "undefined") return true;
    return sessionStorage.getItem(nudgeKey) === "1";
  });
  useEffect(() => {
    if (typeof window === "undefined") return;
    setNudgeDismissed(sessionStorage.getItem(nudgeKey) === "1");
  }, [nudgeKey]);
  const dismissNudge = () => {
    if (typeof window !== "undefined") sessionStorage.setItem(nudgeKey, "1");
    setNudgeDismissed(true);
  };

  return (
    <div className="space-y-5 font-stack-body">
      {/* Layer 5 — Hero Benefits Alliance, shown on top for Hero Stack members */}
      {showHeroLayer && (
        <Card className="border-warm-tan border-l-4 bg-primary p-5 text-primary-foreground">
          <p className="mb-1 text-[10px] font-bold uppercase tracking-[0.18em] text-warm-tan">
            Layer 5 · Your hero layer
          </p>
          <div className="mb-2 flex flex-wrap items-end gap-3">
            <span className="font-stack-heading text-3xl font-bold tabular-nums sm:text-4xl">
              ${HBA_AVERAGE_REWARD.toLocaleString()}
            </span>
            <span className="pb-1 text-sm text-primary-foreground/80">
              {heroLayerLabel}
            </span>
          </div>
          <p className="mb-4 text-sm leading-relaxed text-primary-foreground/80">
            Hero Benefits Alliance rewards at closing, reduced lending fees, and discounted title and inspection —
            on top of the grants below. This is an average reported by HBA, not a guaranteed amount, so it is not
            included in your Stack total.
          </p>
          {heroPhoneProvided && (
            <p className="mb-4 rounded-lg border border-primary-foreground/15 bg-primary-foreground/10 px-3 py-2 text-xs text-primary-foreground/85">
              We can help verify this with the contact number you provided — choose Randy or HBA next.
            </p>
          )}
          <Button
            asChild
            className="bg-accent text-accent-foreground hover:bg-accent/90"
          >
            <Link to="/lp/hero-stack/thank-you" onClick={claimHeroLayer}>Claim my hero layer</Link>
          </Button>
        </Card>
      )}

      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
        <Card className="overflow-hidden border-primary/20 bg-card shadow-sm">
          <div className="p-5 sm:p-7">
          <div className="mb-5 flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-2 rounded-full bg-secondary px-3 py-1 text-xs font-semibold text-secondary-foreground">
              <CheckCircle2 className="h-3.5 w-3.5" /> Your personalized Stack
            </span>
            <span className="text-xs text-muted-foreground">ZIP {data.zip}</span>
          </div>
          {hasAmounts ? (
            <>
              <p className="text-xs font-semibold uppercase text-muted-foreground mb-1">
                {lifemart ? "Your stacked total" : "Preview total"}
              </p>
              <div className="flex items-end gap-3 mb-2">
                <AnimatedTotal
                  value={displayedTotal}
                  className="font-stack-heading text-4xl sm:text-5xl font-bold text-primary tabular-nums"
                />
                <span className="text-sm text-muted-foreground pb-2">potential assistance</span>
              </div>
            </>
          ) : (
            <>
              <p className="text-xs font-semibold uppercase text-muted-foreground mb-1">
                We found your starting point
              </p>
              <div className="flex items-end gap-3 mb-2">
                <AnimatedTotal
                  value={programCount}
                  prefix=""
                  className="font-stack-heading text-4xl sm:text-5xl font-bold text-primary tabular-nums"
                />
                <span className="text-sm text-muted-foreground pb-2">
                  program{programCount === 1 ? "" : "s"} you may qualify for
                </span>
              </div>
            </>
          )}
          <p className="text-sm text-muted-foreground mb-4">
            {hasAmounts ? "Combined across" : "Across"} {layersWithPrograms.length} layer
            {layersWithPrograms.length !== 1 ? "s" : ""} — ZIP {data.zip}
            {data.employer_name ? ` · ${data.employer_name}` : ""}
            {!hasAmounts && layersWithPrograms.length > 0 && (
              <>
                {" · "}
                {layersWithPrograms
                  .map((l) => `${data.layers[l].length} ${LAYER_META[l].label.toLowerCase()}`)
                  .join(", ")}
              </>
            )}
          </p>
          {!hasAmounts && programCount > 0 && (
            <div className="mb-4 border-t border-border pt-3">
              <button type="button" onClick={() => setAmountExplanationOpen((open) => !open)} className="flex w-full items-center justify-between gap-3 text-left text-xs font-medium text-foreground">
                Amounts vary by income, loan size, and available funds
                <ChevronDown className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${amountExplanationOpen ? "rotate-180" : ""}`} />
              </button>
              {amountExplanationOpen && (
                <p className="mt-2 max-w-xl text-xs leading-relaxed text-muted-foreground">
                  These programs publish awards case by case. A lender familiar with your Stack can help confirm the actual amounts available to you.
                </p>
              )}
            </div>
          )}
          {!lifemart && lockedAmount > 0 && (
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <span className="inline-flex items-center gap-1.5 rounded-full border border-accent/40 bg-accent/10 px-3 py-1 text-xs font-semibold text-foreground">
                <Lock className="h-3 w-3 text-accent" />
                + {fmt(lockedAmount)} more locked
              </span>
              <Button asChild size="sm" variant="ghost" className="h-7 text-xs text-accent hover:text-accent hover:bg-accent/10">
                <Link to="/lifemart">Unlock full total — free with LifeMart</Link>
              </Button>
            </div>
          )}
          {breakdown.length > 0 && hasAmounts && (
            <LayerBreakdown items={breakdown} total={data.total_estimated} />
          )}
          </div>
          <div className="grid grid-cols-2 border-t border-border bg-secondary/45 sm:grid-cols-4">
            {ORDER.map((layer) => (
              <div key={layer} className="border-b border-r border-border px-3 py-3 last:border-r-0 sm:border-b-0">
                <p className="text-[11px] text-muted-foreground">{LAYER_META[layer].label}</p>
                <p className="font-stack-heading text-lg font-bold text-foreground">{data.layers[layer].length}</p>
              </div>
            ))}
          </div>
        </Card>
      </motion.div>

      <section aria-labelledby="stack-plan-heading" className="pt-2">
        <div className="mb-5">
          <p className="text-xs font-semibold uppercase text-accent">Your next steps</p>
          <h2 id="stack-plan-heading" className="font-stack-heading mt-1 text-2xl font-bold text-foreground">A simpler path through your results</h2>
        </div>

        <div className="relative space-y-4 before:absolute before:bottom-6 before:left-5 before:top-6 before:w-px before:bg-border sm:before:left-6">
          <Card className="relative overflow-hidden border-primary/25 bg-card p-5 pl-14 shadow-sm sm:p-6 sm:pl-16">
            <span className="absolute left-3 top-5 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-primary font-stack-heading text-sm font-bold text-primary-foreground sm:left-3.5">1</span>
            <p className="text-xs font-semibold uppercase text-primary">Review your best matches</p>
            <h3 className="font-stack-heading mt-1 text-xl font-bold text-foreground">Start with these three</h3>
            <div className="mt-4 divide-y divide-border rounded-lg border border-border bg-background">
              {topMatches.map((program) => {
                const amount = isAmountLike(program.amount_text) ? program.amount_text : programAmount(program) ? fmt(programAmount(program)) : "Amount varies";
                return (
                  <div key={`${program.layer}-${program.id || program.name}`} className="flex items-center justify-between gap-4 px-4 py-3">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-foreground">{program.name}</p>
                      <p className="text-xs text-muted-foreground">{LAYER_META[program.layer].label}</p>
                    </div>
                    <span className="shrink-0 text-xs font-semibold text-primary">{amount}</span>
                  </div>
                );
              })}
            </div>
            <Button type="button" variant="ghost" size="sm" className="mt-3 px-0 text-primary" onClick={revealAllLayers}>Review all {programCount} programs <ArrowRight className="ml-1 h-4 w-4" /></Button>
          </Card>

          <Card className="relative border-border bg-card p-5 pl-14 sm:p-6 sm:pl-16">
            <span className="absolute left-3 top-5 z-10 flex h-9 w-9 items-center justify-center rounded-full border border-warm-tan bg-secondary font-stack-heading text-sm font-bold text-secondary-foreground sm:left-3.5">2</span>
            <p className="text-xs font-semibold uppercase text-muted-foreground">Confirm your fit</p>
            <h3 className="font-stack-heading mt-1 text-lg font-bold text-foreground">{hasQual ? "Your answers are shaping these matches" : "Turn ZIP matches into personal matches"}</h3>
            <p className="mt-1 text-sm text-muted-foreground">Income, timing, savings, and buyer status can change which programs fit.</p>
            <div className="mt-4">{qualificationPrompt ?? (!hasQual ? <Button asChild variant="outline"><Link to="/profile/complete?from=stack">Check what I qualify for</Link></Button> : null)}</div>
          </Card>

          <Card className="relative border-accent/35 bg-card p-5 pl-14 sm:p-6 sm:pl-16">
            <span className="absolute left-3 top-5 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-accent font-stack-heading text-sm font-bold text-accent-foreground sm:left-3.5">3</span>
            <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-xs font-semibold uppercase text-accent">Recommended next move</p>
                <h3 className="font-stack-heading mt-1 text-xl font-bold text-foreground">
                  {showHeroLayer ? "Claim your Hero Benefits layer" : "Find lenders who work with your Stack"}
                </h3>
                <p className="mt-1 max-w-lg text-sm text-muted-foreground">
                  {showHeroLayer
                    ? heroPhoneProvided
                      ? "You gave us a contact number, so the handoff can be quick. Choose Randy or direct HBA enrollment next."
                      : "The fifth layer is the promised Hero Stack payoff. Choose Randy or direct HBA enrollment next."
                    : "See the lenders who work with these programs and choose who reaches out to you."}
                </p>
              </div>
              {showHeroLayer && (
                <Button asChild className="shrink-0 bg-accent text-accent-foreground hover:bg-accent/90">
                  <Link to="/lp/hero-stack/thank-you" onClick={claimHeroLayer}>Claim my hero layer</Link>
                </Button>
              )}
            </div>
            <StackProfileQuestion
              className="mt-5"
              onSaved={(attrs) => setQual((q) => ({ ...((q ?? {}) as any), ...attrs }))}
            />
            <PhoneCapturePrompt placement="stack_results" className="mt-5" />
            <HbaWebinarInvite placement="stack_results" className="mt-5" />
            {!showHeroLayer && (
              <TopLenderHandoff state={data.state} programs={allProgramNames} fallbackHref={lenderMatchHref} />
            )}
            <div className="mt-5 flex flex-wrap gap-2 border-t border-border pt-4">
              {showHeroLayer && <Button asChild size="sm" variant="outline"><Link to={lenderMatchHref}>Match my Stack</Link></Button>}
              {onSave ? <Button size="sm" variant="outline" onClick={onSave}><Bookmark className="mr-1.5 h-4 w-4" /> Save my Stack</Button> : <Button asChild size="sm" variant="outline"><Link to="/auth?returnTo=/grant-finder"><Bookmark className="mr-1.5 h-4 w-4" /> Sign in to save</Link></Button>}
              <Button size="sm" variant="ghost" onClick={() => openHomie({ source: "grant-finder:next-steps", prompt: `Walk me through my Stack for ZIP ${data.zip} and tell me which programs to review first.` })}><MessageCircle className="mr-1.5 h-4 w-4" /> Ask Homie</Button>
            </div>
          </Card>
        </div>
      </section>

      {/* Proactive Homie nudge — one-time, snapshot-scoped, dismissible */}
      <AnimatePresence>
        {homieNudge && !nudgeDismissed && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
          >
            <div className="flex items-start gap-3 rounded-xl border border-accent/40 bg-accent/5 p-3 sm:p-4">
              <div className="h-8 w-8 shrink-0 rounded-full bg-accent/15 flex items-center justify-center">
                <Sparkles className="h-4 w-4 text-accent" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs uppercase tracking-wider text-accent font-semibold mb-0.5">Homie noticed</p>
                <p className="text-sm text-foreground">{homieNudge.label}</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant="default"
                    className="h-7 text-xs"
                    onClick={() => {
                      openHomie({ source: `grant-finder:nudge:${homieNudge.key}`, prompt: homieNudge.prompt });
                      dismissNudge();
                    }}
                  >
                    Ask Homie
                  </Button>
                  <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={dismissNudge}>
                    Not now
                  </Button>
                </div>
              </div>
              <button
                type="button"
                aria-label="Dismiss"
                onClick={dismissNudge}
                className="text-muted-foreground hover:text-foreground p-1 -mr-1 -mt-1"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {!data.employer_name && (
        <EmployerInlineAsk onEmployerAdded={onEmployerAdded} />
      )}

      {/* Member-to-HR referral: only when we have an employer but no confirmed benefit */}
      {data.employer_name && data.layers.employer.length === 0 && (
        <EmployerReferralFlow employerName={data.employer_name} />
      )}

      <section className="border-t border-border pt-5">
        <button type="button" onClick={() => setSupportOpen((open) => !open)} className="flex w-full items-center justify-between gap-4 text-left">
          <div>
            <h2 className="font-stack-heading text-lg font-bold text-foreground">More ways we can help</h2>
            <p className="text-sm text-muted-foreground">Credit readiness, nonprofit alternatives, and program guidance</p>
          </div>
          <ChevronDown className={`h-5 w-5 shrink-0 text-muted-foreground transition-transform ${supportOpen ? "rotate-180" : ""}`} />
        </button>
        {supportOpen && (
          <div className="mt-4 grid gap-3 sm:grid-cols-3">
            <Link to="/credit-readiness" className="rounded-lg border border-border bg-card p-4 transition hover:border-primary/40"><CreditCard className="mb-3 h-5 w-5 text-primary" /><p className="text-sm font-semibold text-foreground">Check credit readiness</p><p className="mt-1 text-xs text-muted-foreground">See a common qualification blocker.</p></Link>
            <Link to="/partners" className="rounded-lg border border-border bg-card p-4 transition hover:border-primary/40"><Handshake className="mb-3 h-5 w-5 text-primary" /><p className="text-sm font-semibold text-foreground">Explore nonprofit paths</p><p className="mt-1 text-xs text-muted-foreground">Compare counseling and no-down-payment alternatives.</p></Link>
            <button type="button" onClick={() => openHomie({ source: "grant-finder:support", prompt: `Explain my ${programCount} Stack matches for ZIP ${data.zip} in plain English.` })} className="rounded-lg border border-border bg-card p-4 text-left transition hover:border-primary/40"><MessageCircle className="mb-3 h-5 w-5 text-primary" /><p className="text-sm font-semibold text-foreground">Ask Homie</p><p className="mt-1 text-xs text-muted-foreground">Get plain-English program guidance.</p></button>
          </div>
        )}
      </section>

      {/* Layers */}
      <div ref={layersRef} className="scroll-mt-24" aria-hidden="true" />
      {ORDER.map((layer) => {
        const meta = LAYER_META[layer];
        const items = data.layers[layer];
        const visibleItems = lifemart ? items : items.slice(0, PREVIEW_PER_LAYER);
        const lockedCount = lifemart ? 0 : Math.max(0, items.length - PREVIEW_PER_LAYER);
        const Icon = meta.icon;
        const isEmployerEmpty = layer === "employer" && items.length === 0;
        return (
          <Card key={layer} className="overflow-hidden">
            <button
              type="button"
              onClick={() => toggle(layer)}
              className="w-full p-4 flex items-center justify-between hover:bg-muted/30 transition"
            >
              <div className="flex items-center gap-3 text-left">
                <div className={`h-10 w-10 rounded-full bg-muted flex items-center justify-center ${meta.color}`}>
                  <Icon className="h-5 w-5" />
                </div>
                <div>
                  <div className="font-semibold text-foreground flex items-center gap-2">
                    {meta.label}
                    <Badge variant="outline" className="text-xs font-normal">
                      {items.length} {items.length === 1 ? "program" : "programs"}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground">{meta.tagline}</p>
                </div>
              </div>
              <ChevronDown className={`h-5 w-5 text-muted-foreground transition-transform ${openLayers[layer] ? "rotate-180" : ""}`} />
            </button>

            <AnimatePresence initial={false}>
              {openLayers[layer] && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="overflow-hidden"
                >
                  <div className="px-4 pb-4 space-y-3">
                    {items.length === 0 && layer !== "employer" && (
                      <div className="rounded-lg border border-dashed p-4 bg-muted/20">
                        {layer === "federal" ? (
                          <>
                            <p className="text-sm text-foreground mb-1">
                              Nothing local surfaced here — but the national programs always apply.
                            </p>
                            <p className="text-xs text-muted-foreground mb-3">
                              FHA (3.5% down), VA (0% down for service members and veterans), USDA (0% down in
                              eligible rural areas), HUD Good Neighbor Next Door, HUD $100 Down, and Section 184
                              for Native American buyers are open nationwide.
                            </p>
                          </>
                        ) : (
                          <>
                            <p className="text-sm text-foreground mb-1">
                              We don't have a {meta.label.toLowerCase()} program on file for this ZIP yet.
                            </p>
                            <p className="text-xs text-muted-foreground mb-3">
                              A HUD-approved housing counselor{data.state ? ` in ${data.state}` : ""} can tell you
                              what's funded right now — the service is free — and the federal programs above still
                              apply to you.
                            </p>
                          </>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs"
                          onClick={() =>
                            openHomie({
                              source: "grant-finder:layer-empty",
                              prompt:
                                layer === "federal"
                                  ? `My Stack for ZIP ${data.zip} returned no federal program. Walk me through FHA, VA, USDA, Good Neighbor Next Door, HUD $100 Down, and Section 184 in plain English and tell me which I should look into.`
                                  : `My Stack for ZIP ${data.zip} found no ${meta.label.toLowerCase()} assistance program. What should I do instead — who do I call, and what free help exists near me?`,
                            })
                          }
                        >
                          Ask Homie what applies to me
                        </Button>
                      </div>
                    )}


                    {isEmployerEmpty && (
                      <div className="rounded-lg border border-dashed p-4 bg-muted/20">
                        {lifemart && data.employer_name ? (
                          <>
                            <div className="flex items-center gap-2 mb-2">
                              <span className="inline-flex items-center gap-1 rounded-full bg-accent/15 text-accent px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider">
                                <BadgeCheck className="h-3 w-3" /> LifeMart concierge
                              </span>
                            </div>
                            <p className="text-sm text-foreground mb-2">
                              No homebuyer benefit confirmed at <strong>{data.employer_name}</strong> yet — most HR teams have one they don't advertise.
                            </p>
                            <p className="text-xs text-muted-foreground mb-3">
                              Let Homie draft a short, polished email you can send HR asking about down-payment or closing-cost assistance.
                            </p>
                            <div className="flex flex-wrap gap-2">
                              <Button
                                size="sm"
                                onClick={() =>
                                  openHomie({
                                    source: "grant-finder:lifemart-hr-draft",
                                    prompt: `I work at ${data.employer_name}. Draft a short, professional email I can send my HR / benefits team asking whether they offer any homebuyer assistance — down payment help, closing cost credit, forgivable loans, or partnerships. Tone: warm, informed, easy to say yes to. End with a clear ask for a quick reply.`,
                                  })
                                }
                                className="gap-1.5"
                              >
                                <Mail className="h-3.5 w-3.5" /> Draft my HR ask with Homie
                              </Button>
                              <Button asChild size="sm" variant="outline">
                                <Link to="/employers/claim">Suggest my employer</Link>
                              </Button>
                            </div>
                          </>
                        ) : data.employer_name ? (
                          <>
                            <p className="text-sm text-foreground mb-2">
                              We couldn't confirm a homebuyer benefit at <strong>{data.employer_name}</strong>.
                            </p>
                            <p className="text-xs text-muted-foreground mb-3">
                              Many employers offer down payment match or closing cost help but don't advertise it. Ask HR — or have them claim a profile.
                            </p>
                            <Button asChild size="sm" variant="outline">
                              <Link to="/employers/claim">Suggest my employer</Link>
                            </Button>
                          </>
                        ) : (
                          <p className="text-sm text-muted-foreground">
                            Add your employer above to unlock employer-funded benefits.
                          </p>
                        )}
                      </div>
                    )}

                    {visibleItems.map((p, i) => {
                      const verdict = hasQual ? scoreProgram(p, qual) : null;
                      return (
                      <div key={(p.id || p.name) + i} className="rounded-lg border bg-background">
                        <button
                          type="button"
                          onClick={() => setOpenProgram((current) => current === `${layer}-${i}` ? null : `${layer}-${i}`)}
                          className="grid w-full grid-cols-[minmax(0,1fr)_auto] items-center gap-3 p-4 text-left"
                          aria-expanded={openProgram === `${layer}-${i}`}
                        >
                          <div className="min-w-0">
                            <p className="font-semibold leading-snug text-foreground">{p.name}</p>
                            {verdict && <p className="mt-1 text-[11px] font-medium text-muted-foreground">{verdict.label}</p>}
                          </div>
                          <div className="flex min-w-0 items-center gap-2">
                            <span className="max-w-24 text-right text-[11px] font-semibold leading-tight text-primary sm:max-w-40 sm:text-xs">
                              {isAmountLike(p.amount_text) ? p.amount_text : programAmount(p) ? fmt(programAmount(p)) : "Amount varies"}
                            </span>
                            <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${openProgram === `${layer}-${i}` ? "rotate-180" : ""}`} />
                          </div>
                        </button>
                        <AnimatePresence initial={false}>
                        {openProgram === `${layer}-${i}` && (
                          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                          <div className="border-t border-border px-4 pb-4 pt-3">
                            <div className="mb-3 flex flex-wrap gap-2">
                              {p.verified && <Badge variant="secondary" className="text-[10px] gap-1"><ShieldCheck className="h-3 w-3" /> Verified</Badge>}
                              {p.source === "ai" && !p.verified && <Badge variant="outline" className="text-[10px] gap-1"><Sparkles className="h-3 w-3" /> AI-sourced</Badge>}
                            </div>
                            {p.provider && <p className="mb-2 text-xs text-muted-foreground"><span className="font-medium text-foreground">Provider: </span>{p.provider}</p>}
                            {benefitDescription(p.amount_text) && <p className="mb-2 text-xs text-muted-foreground"><span className="font-medium text-foreground">What you get: </span>{benefitDescription(p.amount_text)}</p>}
                            {verdict && <p className="mb-2 text-xs text-muted-foreground">{verdict.reason}</p>}
                            {p.eligibility_summary && <p className="text-xs text-muted-foreground"><span className="font-medium text-foreground">Eligibility: </span>{p.eligibility_summary}</p>}
                            {p.stackable_with && p.stackable_with.length > 0 && <div className="mt-3 flex flex-wrap items-center gap-1"><span className="text-[10px] text-muted-foreground">Stacks with:</span>{p.stackable_with.map((s) => <Badge key={s} variant="outline" className="text-[10px]">{s}</Badge>)}</div>}
                          <div className="mt-3 flex flex-wrap items-center gap-2 pt-3 border-t">
                          <Button asChild size="sm" variant="default" className="h-7 text-xs">
                            <Link
                              to={`/lender-match?source=stack&zip=${encodeURIComponent(data.zip)}&program=${encodeURIComponent(p.name)}`}
                            >
                              Get matched with a lender
                            </Link>
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs"
                            onClick={() =>
                              openHomie({
                                source: "grant-finder:program",
                                prompt: `Tell me how I'd qualify for "${p.name}"${p.provider ? ` from ${p.provider}` : ""} in ZIP ${data.zip}, and what to ask my lender.`,
                              })
                            }
                          >
                            Ask Homie about this
                          </Button>
                          {lifemart && layer === "employer" && data.employer_name && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 text-xs gap-1.5"
                              onClick={() =>
                                openHomie({
                                  source: "grant-finder:lifemart-hr-confirm",
                                  prompt: `I work at ${data.employer_name} and Home Approach found "${p.name}" as a possible homebuyer benefit. Draft a short email I can send HR to confirm the program exists, how to enroll, and any deadlines.`,
                                })
                              }
                            >
                              <Mail className="h-3.5 w-3.5" /> Ask HR to confirm
                            </Button>
                          )}
                        </div>
                          </div>
                          </motion.div>
                        )}
                        </AnimatePresence>
                      </div>
                      );
                    })}

                    {lockedCount > 0 && (
                      <>
                        <div className="flex items-center justify-between gap-3 rounded-lg border border-dashed border-accent/40 bg-accent/5 p-3">
                          <p className="text-xs text-foreground flex items-center gap-2">
                            <Lock className="h-3.5 w-3.5 text-accent shrink-0" />
                            <span>
                              <strong>+{lockedCount}</strong> more {meta.label.toLowerCase()} program{lockedCount === 1 ? "" : "s"} — unlocked free with LifeMart.
                            </span>
                          </p>
                          <Button asChild size="sm" variant="outline" className="h-7 text-xs shrink-0">
                            <Link to="/lifemart">Unlock</Link>
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </Card>
        );
      })}

      <p className="text-xs text-muted-foreground flex items-center gap-1.5 italic pt-2">
        <AlertCircle className="h-3 w-3" />
        Verified weekly by Home Approach. We don't link out — your lender match walks you through each application so you don't hit dead pages or stale forms.
      </p>

      <p className="border-t border-border pt-4 text-[11px] italic text-muted-foreground">
        Any closing rewards from Home Approach are funded out-of-pocket by Home Approach — not by lenders, agents, or partners.
      </p>
    </div>
  );
}